import type { BookChapter, BookSection } from '@/components/books/book';
import { DEFAULT_BOOK_COVER_COLOR, isBookCoverColor, type BookCardBook, type BookCoverColor } from '@/components/books/book-card';
import { zyAuthor } from '@/lib/servers';
import {
    buildTocReorderDelta,
    tryBuildMoveChapterOps,
    tryBuildMoveSectionOps,
    type TocReorderOp,
    type TocReorderUpdate,
} from '@/lib/toc-tree';
import type { GetSubjectBooksResponse, MaSubjectBookWithoutVariant, MaSubjectSummary } from '@/lib/zybooks-api';

export interface SubjectSummary {
    id: string;
    name: string;
    displayName: string;
    coverColor: BookCoverColor;
}

export interface SubjectBookVariant {
    id: string;
    label: string;
}

export interface SubjectReleaseRow {
    releaseNumber: number;
    releaseId: number;
    status: string;
    booksByVariantId: Record<string, BookCardBook>;
}

export interface SubjectDetail {
    subject: SubjectSummary;
    variants: SubjectBookVariant[];
    devBooks: BookCardBook[];
    releaseRows: SubjectReleaseRow[];
}

export interface MaBookListRow {
    zybook_code: string;
    zybook_id: number;
    title: string;
    development: boolean;
    cover_color?: string | null;
}

export type MaPlacement = 'included' | 'excluded';

export interface MaTocSectionRow {
    chapter_number: number;
    section_number: number;
    section_title: string;
    short_section_title: string | null;
    ma_document_id: number;
    document_name: string;
    section_guid: string;
    placement: MaPlacement;
    canonical_section_id?: number | null;
    canonical_section_last_updated?: string | null;
    document_last_edited?: string | null;
    ma_test_question_document_ids: number[];
    ma_test_question_document_last_edited?: string | null;
    ma_zybook_document_id: number;
    ma_zybook_chapter_id: number;
    chapter_title: string | null;
    chapter_short_title: string | null;
    test_bank_placement: MaPlacement;
}

export interface MaSharedBookRef {
    zybook_code: string;
    title: string;
    is_placed: boolean;
    is_trashed: boolean;
}

export interface MaUnplacedSectionRow {
    ma_zybook_document_id: number;
    ma_document_id: number;
    section_title: string;
    short_section_title: string | null;
}

export interface MaDeletedMetadata {
    chapter_number: number | null;
    section_number: number | null;
    chapter_title: string | null;
    section_title: string;
}

export interface MaDeletedSectionRow {
    ma_zybook_document_id: number;
    ma_document_id: number;
    section_title: string;
    short_section_title: string | null;
    deleted_at: string;
    deleted_metadata: MaDeletedMetadata;
}

export interface MaBookTocPayload {
    success: boolean;
    zybook_code: string;
    zybook_id: number;
    title: string;
    development: boolean;
    cover_color?: string | null;
    sections: MaTocSectionRow[];
    unplaced_sections: MaUnplacedSectionRow[];
    deleted_sections: MaDeletedSectionRow[];
    shared_documents: Record<string, MaSharedBookRef[]>;
}

export interface MaBookSectionPayload {
    success: boolean;
    state: 'ok' | 'section-not-found';
    book: MaBookListRow;
    section?: MaTocSectionRow;
}

function bookCoverFromMaApi(value: string | null | undefined): BookCoverColor {
    const normalized = value?.trim().toLowerCase();
    if (normalized && isBookCoverColor(normalized)) {
        return normalized;
    }
    return DEFAULT_BOOK_COVER_COLOR;
}

export function bookCardFromApiRow(row: MaBookListRow): BookCardBook {
    return {
        id: row.zybook_code,
        zybook_id: row.zybook_id,
        title: row.title,
        development: row.development,
        coverColor: bookCoverFromMaApi(row.cover_color ?? null),
    };
}

export function subjectSummaryFromApi(subject: MaSubjectSummary): SubjectSummary {
    return {
        id: String(subject.subject_id),
        name: subject.name,
        displayName: subject.name ?? String(subject.subject_id),
        coverColor: bookCoverFromMaApi(subject.cover_color),
    };
}

export function subjectBookToCard(book: MaSubjectBookWithoutVariant, subjectCoverColor: string | null, variantId?: string): BookCardBook {
    return {
        id: book.zybook_code,
        zybook_id: book.zybook_id,
        variantId,
        title: book.title,
        development: book.development,
        coverColor: bookCoverFromMaApi(subjectCoverColor),
        is_ma_zybook: book.is_ma_zybook,
    };
}

export function subjectDetailFromApi(data: Omit<GetSubjectBooksResponse, 'success'>): SubjectDetail {
    const coverColor = bookCoverFromMaApi(data.subject.cover_color);
    const subjectName = data.subject.name ?? data.subject.display_name ?? String(data.subject.subject_id);
    const subject: SubjectSummary = {
        id: String(data.subject.subject_id),
        name: subjectName,
        displayName: data.subject.display_name ?? String(data.subject.subject_id),
        coverColor,
    };

    return {
        subject,
        variants: data.variants,
        devBooks: data.dev_books.map(book => subjectBookToCard(book, data.subject.cover_color, book.variant_id)),
        releaseRows: data.release_rows.map(row => ({
            releaseNumber: row.release_number,
            releaseId: row.release_id,
            status: row.status,
            booksByVariantId: Object.fromEntries(
                Object.entries(row.books_by_variant_id).map(([variantId, book]) => [
                    variantId,
                    subjectBookToCard(book, data.subject.cover_color, variantId),
                ]),
            ),
        })),
    };
}

export function isMaBookRequestNotFound(error: unknown): boolean {
    return error instanceof Error && error.message.includes('status 404');
}

export async function fetchMaBooksList(): Promise<BookCardBook[]> {
    const res = await zyAuthor.get<{ success: boolean; books: MaBookListRow[] }>('modern_authoring/books');
    return res.books.map(bookCardFromApiRow);
}

export async function fetchMaBookToc(zybookCode: string): Promise<MaBookTocPayload> {
    return zyAuthor.get<MaBookTocPayload>(`modern_authoring/books/${encodeURIComponent(zybookCode)}/toc`);
}

export async function fetchMaBookSection(zybookCode: string, sectionId: string): Promise<MaBookSectionPayload> {
    return zyAuthor.get<MaBookSectionPayload>(
        `modern_authoring/books/${encodeURIComponent(zybookCode)}/sections/${encodeURIComponent(sectionId)}`,
    );
}

export type MaTocReorderPayload = { ops: TocReorderOp[] } | { updates: TocReorderUpdate[] };

export type MaTocReorderResponse = {
    success: boolean;
};

export function isEmptyTocReorderPayload(payload: MaTocReorderPayload): boolean {
    if ('ops' in payload) {
        return payload.ops.length === 0;
    }
    return payload.updates.length === 0;
}

export function buildTocReorderPayload(before: BookChapter[], after: BookChapter[]): MaTocReorderPayload {
    const chapterOps = tryBuildMoveChapterOps(before, after);
    if (chapterOps !== null) {
        return { ops: chapterOps };
    }

    const sectionOps = tryBuildMoveSectionOps(before, after);
    if (sectionOps !== null) {
        return { ops: sectionOps };
    }

    return { updates: buildTocReorderDelta(before, after) };
}

export async function reorderMaBookToc(zybookCode: string, payload: MaTocReorderPayload): Promise<MaTocReorderResponse> {
    return zyAuthor.post<MaTocReorderResponse>(`modern_authoring/books/${encodeURIComponent(zybookCode)}/toc/reorder`, { body: payload });
}

export interface SubjectTermRelease {
    subject_term_release_id: number | null;
    subject_release_id: number | null;
    academic_term_id: number;
}

/**
 * The subset of the release payload the release wizard reads or has to echo back.
 */
export interface SubjectReleaseDetail {
    release_id: number;
    number: number;
    name: string;
    description: string | null;
    status: string;
    zybook_id: number;
    zybook?: { zybook_code: string; title: string };
    additional_zybook_ids: number[];
    additional_zybooks?: { zybook_id: number; zybook_code: string; title: string }[];
    ordering_zybook_id: number | null;
    ordering_zybook?: { zybook_code: string; title: string } | null;
    creation_date?: string | null;
    zybook_isbn?: string | null;
    about_page_canonical_section_id: number | null;
    lab_about_page_canonical_section_id?: number | null;
    subject_term_releases: SubjectTermRelease[];
}

export interface SubjectReleaseUpdate {
    name: string;
    description: string;
    status: string;
    ordering_zybook_id: number | null;
    subject_term_releases: SubjectTermRelease[];
}

export async function fetchSubjectReleaseDetail(subjectName: string, releaseNumber: number): Promise<SubjectReleaseDetail> {
    const response = await zyAuthor.get<{ success: boolean; releases: SubjectReleaseDetail[] }>(
        `subject/${encodeURIComponent(subjectName)}/releases`,
        { params: { release_numbers: [releaseNumber] } },
    );
    const release = response.releases.find(item => item.number === releaseNumber);
    if (!release) {
        throw new Error(`Release ${releaseNumber} was not found`);
    }
    return release;
}

export async function updateSubjectRelease(
    subjectName: string,
    releaseNumber: number,
    payload: SubjectReleaseUpdate,
): Promise<SubjectReleaseDetail> {
    const response = await zyAuthor.put<{ success: boolean; release: SubjectReleaseDetail }>(
        `subject/${encodeURIComponent(subjectName)}/release/${releaseNumber}`,
        { body: payload },
    );
    return response.release;
}

export interface ReleaseSubjectDraftResponse {
    success: boolean;
    location: string;
}

export interface MaReleaseBookPayload {
    variant_id: string;
    zybook_id: number;
    zybook_code: string;
}

export interface MaReleasePayload {
    books: MaReleaseBookPayload[];
}

/**
 * Starts releasing the subject's draft, publishing its books and creating the next draft. Resolves once the release
 * is queued; track it with `fetchSubjectReleaseStatus`.
 *
 * @param subjectId - ID of the subject whose draft is being released.
 * @param releaseNumber - The release's `number`, which is what the endpoint keys on, not `release_id`.
 */
export async function releaseSubjectDraft(
    subjectId: string | number,
    releaseNumber: number,
    payload: MaReleasePayload,
): Promise<ReleaseSubjectDraftResponse> {
    return zyAuthor.put<ReleaseSubjectDraftResponse>(`subject/${subjectId}/release/${releaseNumber}/release`, {
        body: payload,
        pollAccepted: false,
    });
}

export interface SubjectReleaseProgress {
    stage: string;
    /** Zero-based position of `stage` within the release's stages. */
    stage_index: number;
    stage_count: number;
    completed: number | null;
    total: number | null;
    detail: string | null;
}

export type SubjectReleaseStatus =
    | { in_progress: false }
    | {
          in_progress: true;
          release_number: number;
          releasing_user_id: number;
          releasing_user_name: string;
          started_at: string;
          location: string;
          progress?: SubjectReleaseProgress;
      };

/**
 * Reports whether a release is currently running for the subject.
 *
 * @param subjectId - ID of the subject to check.
 */
export async function fetchSubjectReleaseStatus(subjectId: string | number): Promise<SubjectReleaseStatus> {
    return zyAuthor.get<SubjectReleaseStatus>(`subject/${subjectId}/release/status`);
}

export interface CatalogItem {
    zybook_catalog_id: number;
    zybook_id: number;
    instructor: boolean;
    student: boolean;
    use_as_ordering: boolean;
    zybook: { zybook_code: string; title: string };
}

export interface CatalogItemResponse {
    success: boolean;
    catalog_item: CatalogItem;
}

export interface SubjectCatalogItem extends CatalogItem {
    release_numbers: number[];
}

export interface SubjectCatalogResponse {
    catalog_items: SubjectCatalogItem[];
    release_books: {
        release_number: number;
        release_status: string;
        zybook_id: number;
        zybook: { zybook_code: string; title: string };
    }[];
}

/** Lists catalog entries and published books linked to the subject's releases. */
export async function fetchSubjectCatalog(subjectId: number): Promise<SubjectCatalogResponse> {
    return zyAuthor.get<SubjectCatalogResponse>(`subjects/${subjectId}/catalog_items`);
}

/** Adds an existing released book to the catalog without copying or changing its content. */
export async function addSubjectCatalogItem(
    subjectId: number,
    payload: { release_number: number; zybook_id: number; instructor: boolean; student: boolean; use_as_ordering: boolean },
): Promise<SubjectCatalogItem> {
    const response = await zyAuthor.post<{ success: boolean; catalog_item: SubjectCatalogItem }>(`subjects/${subjectId}/catalog_items`, {
        body: payload,
    });
    return response.catalog_item;
}

/** Removes the catalog listing only; the released book remains intact. */
export async function deleteSubjectCatalogItem(subjectId: number, catalogItemId: number): Promise<void> {
    await zyAuthor.delete(`subjects/${subjectId}/catalog_items/${catalogItemId}`);
}

export async function updateCatalogSettings(
    catalogItemId: number,
    settings: { zybook_id?: number; instructor: boolean; student: boolean; use_as_ordering?: boolean },
): Promise<CatalogItem> {
    const response = await zyAuthor.put<CatalogItemResponse>(`catalog_items/${catalogItemId}`, { body: { metadata: settings } });
    return response.catalog_item;
}

export interface MaExportDocumentResult {
    success: boolean;
    resource_directory_name: string;
    images_path: string;
    hype_animations_path: string;
    error?: { code: number; message: string };
}

export async function exportMaDocument(params: {
    documentName: string;
    devBookName: string;
    contentResources: Record<string, unknown>[];
    testQuestions?: Record<string, unknown>[];
}): Promise<MaExportDocumentResult> {
    const result = await zyAuthor.post<MaExportDocumentResult>('modern_authoring/export_document', {
        body: {
            content_resources: params.contentResources,
            document_name: params.documentName,
            dev_book_name: params.devBookName,
            test_questions: params.testQuestions,
        },
    });

    // The endpoint returns 200 with success=false for permission and lookup failures.
    if (!result.success) {
        throw new Error(result.error?.message ?? 'Failed to export document');
    }
    return result;
}

export interface MaAboutPageExportPayload {
    canonicalSectionId?: number | null;
    sectionNumber?: number | null;
    title?: string | null;
    sectionGuid?: string | null;
    gdocId?: string | null;
    contentResources: Record<string, unknown>[];
}

export interface MaUpdateAboutPagesResult {
    success: boolean;
    about_page_canonical_section_id?: number;
    lab_about_page_canonical_section_id?: number | null;
}

export async function updateMaAboutPages(
    zybookCode: string,
    params: { aboutPage: MaAboutPageExportPayload; labAboutPage?: MaAboutPageExportPayload | null },
): Promise<MaUpdateAboutPagesResult> {
    return zyAuthor.post<MaUpdateAboutPagesResult>(`zybook/${encodeURIComponent(zybookCode)}/update_about_pages`, {
        body: {
            about_page: {
                canonical_section_id: params.aboutPage.canonicalSectionId,
                section_number: params.aboutPage.sectionNumber,
                title: params.aboutPage.title,
                section_guid: params.aboutPage.sectionGuid,
                gdoc_id: params.aboutPage.gdocId,
                about_page_content_resource: params.aboutPage.contentResources,
            },
            lab_about_page: params.labAboutPage
                ? {
                      canonical_section_id: params.labAboutPage.canonicalSectionId,
                      section_number: params.labAboutPage.sectionNumber,
                      title: params.labAboutPage.title,
                      section_guid: params.labAboutPage.sectionGuid,
                      gdoc_id: params.labAboutPage.gdocId,
                      lab_about_page_content_resource: params.labAboutPage.contentResources,
                  }
                : null,
        },
    });
}

export interface MaShareSectionResult {
    success: boolean;
    shared: Array<{ zybook_code: string; ma_zybook_document_id: number }>;
    skipped: Array<{
        zybook_code: string;
        reason: 'already_exists' | 'book_not_found' | 'same_book';
        location_label?: string;
    }>;
}

export async function shareMaSection(
    sourceZybookCode: string,
    maDocumentId: number,
    targetZybookCodes: string[],
): Promise<MaShareSectionResult> {
    return zyAuthor.post<MaShareSectionResult>(
        `modern_authoring/books/${encodeURIComponent(sourceZybookCode)}/sections/${maDocumentId}/share`,
        { body: { target_zybook_codes: targetZybookCodes } },
    );
}

export async function trashMaSection(
    zybookCode: string,
    maDocumentId: number,
): Promise<{ success: boolean; deleted_section?: MaDeletedSectionRow }> {
    return zyAuthor.delete(`modern_authoring/books/${encodeURIComponent(zybookCode)}/sections/${maDocumentId}/trash`);
}

export async function trashMaChapter(
    zybookCode: string,
    maZybookChapterId: number,
): Promise<{ success: boolean; ma_zybook_chapter_id: number; section_count: number; deleted_sections?: MaDeletedSectionRow[] }> {
    return zyAuthor.delete(`modern_authoring/books/${encodeURIComponent(zybookCode)}/chapters/${maZybookChapterId}/trash`);
}

export async function restoreMaTrashedSection(
    zybookCode: string,
    maZybookDocumentId: number,
    payload: {
        chapter_number: number;
        section_number: number;
        chapter_title: string;
        short_chapter_title?: string | null;
    },
): Promise<{
    success: boolean;
    ma_zybook_chapter_id?: number;
    ma_test_question_document_ids?: number[];
    ma_test_question_document_last_edited?: string | null;
}> {
    return zyAuthor.post(`modern_authoring/books/${encodeURIComponent(zybookCode)}/trashed_sections/${maZybookDocumentId}/restore`, {
        body: payload,
    });
}

export async function permanentlyDeleteMaTrashedSection(zybookCode: string, maZybookDocumentId: number): Promise<{ success: boolean }> {
    return zyAuthor.delete(`modern_authoring/books/${encodeURIComponent(zybookCode)}/trashed_sections/${maZybookDocumentId}`);
}

export type MaRenameSectionResult = {
    success: boolean;
    section: {
        ma_document_id: number;
        section_title: string;
        short_section_title: string | null;
    };
};

export type MaRenameChapterResult = {
    success: boolean;
    chapter: {
        ma_zybook_chapter_id: number;
        chapter_title: string;
        chapter_short_title: string | null;
    };
};

export async function renameMaSection(
    zybookCode: string,
    maDocumentId: number,
    body: { section_title: string; short_section_title?: string | null },
): Promise<MaRenameSectionResult> {
    return zyAuthor.patch<MaRenameSectionResult>(`modern_authoring/books/${encodeURIComponent(zybookCode)}/sections/${maDocumentId}`, {
        body,
    });
}

export async function renameMaChapter(
    maZybookChapterId: number,
    body: { chapter_title: string; short_chapter_title?: string | null },
): Promise<MaRenameChapterResult> {
    return zyAuthor.patch<MaRenameChapterResult>(`modern_authoring/chapters/${maZybookChapterId}`, { body });
}

export type MaSectionReleaseExclusionPayload = { placement: MaPlacement } | { test_bank_placement: MaPlacement };

export type MaSectionReleaseExclusionResult = {
    success: boolean;
    section: {
        ma_document_id: number;
        placement: MaPlacement;
        test_bank_placement: MaPlacement;
    };
};

export type MaChapterReleaseExclusionResult = {
    success: boolean;
    chapter: {
        ma_zybook_chapter_id: number;
        placement: MaPlacement;
        section_count: number;
    };
};

export async function setMaSectionReleaseExclusion(
    zybookId: number,
    maDocumentId: number,
    body: MaSectionReleaseExclusionPayload,
): Promise<MaSectionReleaseExclusionResult> {
    return zyAuthor.patch<MaSectionReleaseExclusionResult>(
        `modern_authoring/books/${zybookId}/sections/${maDocumentId}/release_exclusion`,
        { body },
    );
}

export async function setMaChapterReleaseExclusion(
    zybookId: number,
    maZybookChapterId: number,
    placement: MaPlacement,
): Promise<MaChapterReleaseExclusionResult> {
    return zyAuthor.patch<MaChapterReleaseExclusionResult>(
        `modern_authoring/books/${zybookId}/chapters/${maZybookChapterId}/release_exclusion`,
        { body: { placement } },
    );
}

export function isChapterFullyExcluded(chapter: BookChapter): boolean {
    return chapter.sections.length > 0 && chapter.sections.every(section => section.placement === 'excluded');
}

export async function placeMaUnplacedSection(
    zybookCode: string,
    maZybookDocumentId: number,
    payload: {
        chapter_number: number;
        section_number: number;
        chapter_title: string;
        short_chapter_title?: string | null;
    },
): Promise<{ success: boolean; ma_zybook_chapter_id?: number }> {
    return zyAuthor.post(`modern_authoring/books/${encodeURIComponent(zybookCode)}/unplaced_sections/${maZybookDocumentId}/place`, {
        body: payload,
    });
}

export function isMaDocumentShared(sharedDocuments: Record<string, MaSharedBookRef[]>, maDocumentId: string): boolean {
    return (sharedDocuments[maDocumentId]?.length ?? 0) > 1;
}

export function filterMaSharedBooksForBook(books: MaSharedBookRef[], currentZybookCode: string): MaSharedBookRef[] {
    return books.filter(book => book.zybook_code !== currentZybookCode);
}

export function buildMaSharedBookHref(options: {
    zybookCode: string;
    maDocumentId: string;
    isPlaced: boolean;
    isTrashed?: boolean;
}): string {
    const bookPath = `/books/${encodeURIComponent(options.zybookCode)}`;
    if (options.isPlaced) {
        return `${bookPath}/${encodeURIComponent(options.maDocumentId)}`;
    }
    if (options.isTrashed) {
        return bookPath;
    }
    const params = new URLSearchParams({
        panel: 'unplaced-sections',
        section: options.maDocumentId,
    });
    return `${bookPath}?${params.toString()}`;
}

export function sectionTestBankPath(zybookCode: string, testQuestionDocumentId: number): string {
    return `/books/${encodeURIComponent(zybookCode)}/${String(testQuestionDocumentId)}`;
}

export function sectionFromTocRow(row: MaTocSectionRow): BookSection {
    const title = row.section_title.trim();
    const shortTitle = row.short_section_title?.trim() || null;
    return {
        id: String(row.ma_document_id),
        number: `${row.chapter_number}.${row.section_number}`,
        title,
        shortTitle,
        canonicalSectionId: row.canonical_section_id ?? null,
        canonicalSectionLastUpdated: row.canonical_section_last_updated ?? null,
        documentLastEditedAt: row.document_last_edited ?? null,
        testQuestionDocumentLastEditedAt: row.ma_test_question_document_last_edited ?? null,
        ma_zybook_chapter_id: row.ma_zybook_chapter_id,
        testQuestionDocumentId: row.ma_test_question_document_ids[0] ?? null,
        placement: row.placement,
        testBankPlacement: row.test_bank_placement,
    };
}

export function isMaSectionSynced(
    section: Pick<
        BookSection,
        'canonicalSectionLastUpdated' | 'documentLastEditedAt' | 'testQuestionDocumentId' | 'testQuestionDocumentLastEditedAt'
    >,
): section is typeof section & { canonicalSectionLastUpdated: string; documentLastEditedAt: string } {
    if (section.documentLastEditedAt == null || section.canonicalSectionLastUpdated == null) {
        return false;
    }

    const sectionIsSynced = new Date(section.canonicalSectionLastUpdated).getTime() >= new Date(section.documentLastEditedAt).getTime();

    // Section has no associated test bank, synced state is equal to the sync state of the section.
    if (section.testQuestionDocumentId == null) {
        return sectionIsSynced;
    }

    if (section.testQuestionDocumentLastEditedAt == null) {
        return false;
    }

    // Section has an associated test bank. Section is synced only if the section AND the test bank is synced.
    const testBankIsSynced =
        new Date(section.canonicalSectionLastUpdated).getTime() >= new Date(section.testQuestionDocumentLastEditedAt).getTime();
    return sectionIsSynced && testBankIsSynced;
}

export function resolveMaDocumentIdForSectionRoute(sectionRouteId: string, sectionRow: MaTocSectionRow): number {
    const requestedId = Number(sectionRouteId);
    if (Number.isInteger(requestedId) && requestedId > 0 && sectionRow.ma_test_question_document_ids.includes(requestedId)) {
        return requestedId;
    }
    return sectionRow.ma_document_id;
}

export function findMaTocSectionByDocumentId(sections: MaTocSectionRow[], sectionDocumentId: string): MaTocSectionRow | undefined {
    return sections.find(
        row =>
            String(row.ma_document_id) === sectionDocumentId ||
            row.ma_test_question_document_ids.some(id => String(id) === sectionDocumentId),
    );
}

function chapterTitlesFromTocRow(row: MaTocSectionRow): { title: string; shortTitle: string | null } {
    if (row.chapter_number === 0) {
        return { title: row.chapter_title?.trim() || 'About', shortTitle: row.chapter_short_title ?? null };
    }
    const title = row.chapter_title?.trim() || `Chapter ${row.chapter_number}`;
    const shortTitle = row.chapter_short_title?.trim() || null;
    return { title, shortTitle };
}

export function groupTocSectionsToChapters(sections: MaTocSectionRow[]): BookChapter[] {
    const map = new Map<number, BookChapter>();
    for (const row of sections) {
        const n = row.chapter_number;
        if (!map.has(n)) {
            const { title, shortTitle } = chapterTitlesFromTocRow(row);
            map.set(n, {
                id: `ch-${n}`,
                number: n,
                title,
                shortTitle,
                ma_zybook_chapter_id: row.ma_zybook_chapter_id,
                sections: [],
            });
        }
        const ch = map.get(n)!;
        ch.sections.push(sectionFromTocRow(row));
    }
    return [...map.values()].sort((a, b) => a.number - b.number);
}
